import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaCheckCircle, FaGlobeAfrica, FaPlus } from "react-icons/fa";
import adminApi from "services/admin-api";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import Modal from "components/global/Modal";
import ConfirmModal from "components/global/ConfirmModal";
import { DataTable } from "components/molecules/table/table";
import {
  CountryCoverageMap,
  EmptyState,
  PageHeader,
  SectionCard,
  StatCard,
} from "components/admin";
import { countryList } from "constants/country-list";
import { countryPresenceForList } from "utils/country-presence";
import { useAuth } from "context/auth-context";
import { hasPortalAction } from "utils/portal-access";
import {
  CountriesPrimaryButton,
  CountriesSecondaryButton,
  CountriesPrimaryButtonSecondary,
  CountriesOptionButton,
  CountriesGridLayout,
  CountriesTextGroup,
  CountriesEmptyStatePanel,
  CountriesInfoRowSecondary,
  CountriesGridLayoutSecondary,
  CountriesSection,
  CountriesActionRow,
  CountriesContentStack,
  CountriesFieldShell,
  CountriesHeaderRow,
  CountriesScrollArea,
  CountriesInfoRow,
  CountriesSearchIcon,
  CountriesFormStack,
  CountriesFieldInput,
  CountriesSearchInput,
  CountriesFieldLabel,
  CountriesLabelText,
  CountriesMetaText,
  CountriesEyebrowText,
  CountriesTitleText,
  CountriesMetaTextSecondary,
  CountriesBodyText,
  CountriesLabelTextSecondary,
  CountriesIconBadge,
  CountriesIconBadgeSecondary,
  CountriesStatusBadge,
} from "pages/app/countries/styles/countries.page.inline.styles";
const normalizeCountryName = (value: string) => value.trim().toLowerCase();
const CountriesPage = () => {
  const queryClient = useQueryClient();
  const { portalAccess } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [countryPickerSearch, setCountryPickerSearch] = useState("");
  const [draftName, setDraftName] = useState("");
  const [editingCountry, setEditingCountry] = useState<string | null>(null);
  const [nextName, setNextName] = useState("");
  const [countryToDelete, setCountryToDelete] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const deferredSearch = useDeferredValue(searchValue);
  const deferredCountryPickerSearch = useDeferredValue(countryPickerSearch);
  const { data: countries = [] } = useQuery({
    queryKey: ["countries"],
    queryFn: () => adminApi.countries.list(),
  });
  const createMutation = useMutation({
    mutationFn: () => adminApi.countries.create(draftName.trim()),
    onSuccess: () => {
      const createdCountryName = draftName.trim();
      toast.success("Country created.");
      queryClient.invalidateQueries({
        queryKey: ["countries"],
      });
      setDraftName("");
      setCountryPickerSearch("");
      setSelectedCountry(createdCountryName || null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to create this country.");
    },
  });
  const updateMutation = useMutation({
    mutationFn: () => adminApi.countries.update(editingCountry || "", nextName.trim()),
    onSuccess: () => {
      toast.success("Country renamed.");
      queryClient.invalidateQueries({
        queryKey: ["countries"],
      });
      setEditingCountry(null);
      setNextName("");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to rename this country.");
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => adminApi.countries.remove(countryToDelete || ""),
    onSuccess: () => {
      toast.success("Country removed.");
      queryClient.invalidateQueries({
        queryKey: ["countries"],
      });
      setCountryToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to remove this country.");
    },
  });
  const filteredCountries = useMemo(
    () =>
      countries.filter((country: any) =>
        country.name.toLowerCase().includes(deferredSearch.trim().toLowerCase()),
      ),
    [countries, deferredSearch],
  );
  const existingCountryNames = useMemo(
    () => new Set(countries.map((country: any) => normalizeCountryName(country.name))),
    [countries],
  );
  const pickerCountries = useMemo(() => {
    const query = deferredCountryPickerSearch.trim().toLowerCase();
    return countryList
      .filter((country) => country.name.toLowerCase().includes(query))
      .sort((left, right) => {
        const leftSelected = existingCountryNames.has(normalizeCountryName(left.name));
        const rightSelected = existingCountryNames.has(normalizeCountryName(right.name));
        if (leftSelected !== rightSelected) {
          return leftSelected ? -1 : 1;
        }
        return left.name.localeCompare(right.name);
      });
  }, [deferredCountryPickerSearch, existingCountryNames]);
  const coverage = useMemo(
    () => countryPresenceForList(filteredCountries.map((country: any) => country.name)),
    [filteredCountries],
  );
  const mappedCount = coverage.filter((country) => country.mapped).length;
  const unmappedCount = coverage.filter((country) => !country.mapped).length;
  const draftAlreadyExists = existingCountryNames.has(normalizeCountryName(draftName));
  const canManageCountries = hasPortalAction(portalAccess, "countries.manage");
  useEffect(() => {
    if (!filteredCountries.length) {
      setSelectedCountry(null);
      return;
    }
    const stillVisible = filteredCountries.some((country: any) => country.name === selectedCountry);
    if (!selectedCountry || !stillVisible) {
      setSelectedCountry(filteredCountries[0].name);
    }
  }, [filteredCountries, selectedCountry]);
  return (
    <AppLayout route="/countries">
      <PageHeader
        title="Countries"
        description={
          canManageCountries
            ? "Manage the geographic footprint used across users, teams, and athlete relationships, with a map view for quick coverage checks."
            : "Review the geographic footprint used across users, teams, athletes, and school records, with a map view for quick coverage checks."
        }
      />

      <CountriesGridLayout>
        <StatCard label="Visible Countries" value={filteredCountries.length} />
        <StatCard label="Pinned On Map" value={mappedCount} />
        <StatCard label="Need Coordinates" value={unmappedCount} />
        <StatCard
          helper="Filtered by the current search input"
          label="Coverage Ratio"
          value={
            filteredCountries.length
              ? `${Math.round((mappedCount / filteredCountries.length) * 100)}%`
              : "0%"
          }
        />
      </CountriesGridLayout>

      <CountriesGridLayoutSecondary>
        <SectionCard
          subtitle="A live footprint of the countries currently stored in the admin dataset"
          title="Coverage Map"
        >
          {filteredCountries.length > 0 ? (
            <CountryCoverageMap
              activeCountry={selectedCountry}
              countries={filteredCountries.map((country: any) => country.name)}
              onCountrySelect={setSelectedCountry}
            />
          ) : (
            <EmptyState
              description="Try a broader search or add your first country to start visualising coverage."
              title="No countries to map"
            />
          )}
        </SectionCard>

        {canManageCountries ? (
          <SectionCard
            subtitle="Create a new country that can be reused by teams and users"
            title="Create Country"
          >
            <CountriesFormStack
              onSubmit={(event) => {
                event.preventDefault();
                createMutation.mutate();
              }}
            >
              <CountriesFieldShell>
                <CountriesSearchIcon />
                <CountriesSearchInput
                  onChange={(event) => setCountryPickerSearch(event.target.value)}
                  placeholder="Search all countries"
                  value={countryPickerSearch}
                />
              </CountriesFieldShell>

              <CountriesHeaderRow>
                <p>
                  {pickerCountries.length} {pickerCountries.length === 1 ? "country" : "countries"}{" "}
                  found
                </p>
                <p>{countries.length} already in your directory</p>
              </CountriesHeaderRow>

              <CountriesScrollArea>
                {pickerCountries.length > 0 ? (
                  pickerCountries.map((country) => {
                    const alreadySelected = existingCountryNames.has(
                      normalizeCountryName(country.name),
                    );
                    const isActive = draftName === country.name;
                    return (
                      <CountriesOptionButton
                        $alreadySelected={alreadySelected}
                        $isActive={isActive}
                        key={country.name}
                        onClick={() => {
                          setDraftName(country.name);
                          if (alreadySelected) {
                            setSelectedCountry(country.name);
                          }
                        }}
                        type="button"
                      >
                        <CountriesInfoRow>
                          <CountriesIconBadgeSecondary $alreadySelected={alreadySelected}>
                            <FaGlobeAfrica size={16} />
                          </CountriesIconBadgeSecondary>
                          <CountriesTextGroup>
                            <CountriesLabelText>{country.name}</CountriesLabelText>
                            <CountriesMetaText>
                              {alreadySelected
                                ? "Already added and highlighted in your admin directory"
                                : "Available to add to your admin directory"}
                            </CountriesMetaText>
                          </CountriesTextGroup>
                        </CountriesInfoRow>

                        <CountriesStatusBadge
                          $alreadySelected={alreadySelected}
                          $isActive={isActive}
                        >
                          {alreadySelected ? <FaCheckCircle size={12} /> : null}
                          {alreadySelected ? "Added" : isActive ? "Selected" : "Pick"}
                        </CountriesStatusBadge>
                      </CountriesOptionButton>
                    );
                  })
                ) : (
                  <EmptyState
                    description="Try a different search term to browse the world country list."
                    title="No countries match this search"
                  />
                )}
              </CountriesScrollArea>

              <CountriesPrimaryButton
                disabled={!draftName.trim() || draftAlreadyExists || createMutation.isPending}
                type="submit"
              >
                <FaPlus size={12} />
                {createMutation.isPending ? "Creating..." : "Add country"}
              </CountriesPrimaryButton>
            </CountriesFormStack>

            <CountriesEmptyStatePanel>
              <CountriesEyebrowText>Picker status</CountriesEyebrowText>
              <CountriesInfoRowSecondary>
                <CountriesIconBadge>
                  <FaGlobeAfrica size={18} />
                </CountriesIconBadge>
                <div>
                  <CountriesTitleText>
                    {draftName || selectedCountry || "No country selected"}
                  </CountriesTitleText>
                  <CountriesMetaTextSecondary>
                    {draftName
                      ? draftAlreadyExists
                        ? "This country already exists in the directory and is highlighted above."
                        : "This country is ready to be added to the directory."
                      : "Choose a country from the searchable list to preview its status."}
                  </CountriesMetaTextSecondary>
                </div>
              </CountriesInfoRowSecondary>
              <CountriesBodyText>
                Countries already stored in the system are highlighted in green, and clicking one
                also focuses it in the workspace.
              </CountriesBodyText>
            </CountriesEmptyStatePanel>
          </SectionCard>
        ) : (
          <SectionCard
            subtitle="Catalog changes are restricted to platform managers"
            title="Read-Only Access"
          >
            <EmptyState
              description="Your role can reference country coverage across schools, teams, and users, but only catalog managers can add, rename, or remove countries."
              title="Country edits unavailable"
            />
          </SectionCard>
        )}
      </CountriesGridLayoutSecondary>

      <CountriesSection>
        <SectionCard
          subtitle="Search, rename, or remove existing country records with pagination"
          title="Country Directory"
        >
          <DataTable
            canEdit={canManageCountries}
            canRemove={canManageCountries}
            columns={[
              {
                label: "Country",
                accessor: (row: any) => (
                  <div>
                    <CountriesLabelTextSecondary>{row.name}</CountriesLabelTextSecondary>
                    <CountriesMetaText>
                      {countryPresenceForList([row.name])[0]?.mapped
                        ? "Mapped on coverage globe"
                        : "Stored, but not yet mapped"}
                    </CountriesMetaText>
                  </div>
                ),
              },
              {
                label: "Coverage",
                accessor: (row: any) =>
                  countryPresenceForList([row.name])[0]?.mapped
                    ? "Mapped"
                    : "Needs coordinate mapping",
              },
              {
                label: "Usage",
                accessor: () => "Available to teams and users",
              },
            ]}
            data={filteredCountries}
            emptyDescription="Create your first country record to start building platform geography."
            emptyTitle="No countries match this search"
            onEdit={
              canManageCountries
                ? (row) => {
                    setEditingCountry(row.name);
                    setNextName(row.name);
                  }
                : undefined
            }
            onRemove={
              canManageCountries
                ? (_, row) => setCountryToDelete(String(row?.name || ""))
                : undefined
            }
            onView={(name, row) => setSelectedCountry(String(row?.name || name))}
            rowKey="name"
            toolbar={
              <CountriesFieldInput
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search countries"
                value={searchValue}
              />
            }
          />
        </SectionCard>
      </CountriesSection>

      {canManageCountries ? (
        <>
          <Modal
            isOpen={Boolean(editingCountry)}
            onClose={() => {
              setEditingCountry(null);
              setNextName("");
            }}
            size="md"
            title={editingCountry ? `Rename ${editingCountry}` : "Rename country"}
            actions={
              <CountriesActionRow>
                <CountriesSecondaryButton
                  onClick={() => {
                    setEditingCountry(null);
                    setNextName("");
                  }}
                  type="button"
                >
                  Cancel
                </CountriesSecondaryButton>
                <CountriesPrimaryButtonSecondary
                  disabled={!nextName.trim() || updateMutation.isPending}
                  onClick={() => updateMutation.mutate()}
                  type="button"
                >
                  {updateMutation.isPending ? "Saving..." : "Save name"}
                </CountriesPrimaryButtonSecondary>
              </CountriesActionRow>
            }
          >
            <CountriesContentStack>
              <CountriesFieldLabel>Updated country name</CountriesFieldLabel>
              <CountriesFieldInput
                onChange={(event) => setNextName(event.target.value)}
                placeholder="Country name"
                value={nextName}
              />
            </CountriesContentStack>
          </Modal>

          <ConfirmModal
            cancelLabel="Keep country"
            confirmLabel="Delete country"
            description={`Delete ${
              countryToDelete || "this country"
            }? The backend will block this if linked records still exist.`}
            isLoading={deleteMutation.isPending}
            isOpen={Boolean(countryToDelete)}
            onClose={() => setCountryToDelete(null)}
            onConfirm={() => deleteMutation.mutate()}
            title="Remove country?"
            tone="danger"
          />
        </>
      ) : null}
    </AppLayout>
  );
};
export default CountriesPage;
